// Mini map rendered above the Single-Row Details "Spatial" group (T11). Decodes every spatial cell
// in the group through the ASYNC lazy API (never the sync core) and paints them on one map. Renders
// nothing until at least one cell decodes, so a non-geometry value never leaves an empty map frame.

import { useEffect, useMemo, useState } from 'react';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { decodeSpatialBatch } from '../../spatial/decode/decodeSpatialBatch';
import { MAX_EAGER_DECODE_BYTES } from '../../spatial/decode/decodeLimits';
import {
  SpatialMapHost,
  type SpatialMapFeature,
  type SpatialMapLayerInput,
} from '../../spatial/map/SpatialMapLazy';

type SpatialCellInputData = { name: string; value: unknown; dataType?: string };

type SpatialRowGroupMapProps = {
  engine: DatabaseEngineId;
  cells: ReadonlyArray<SpatialCellInputData>;
};

export function SpatialRowGroupMap({ engine, cells }: SpatialRowGroupMapProps) {
  const [features, setFeatures] = useState<SpatialMapFeature[]>([]);

  const items = useMemo(
    () =>
      cells
        .filter((c) => c.value !== null && c.value !== undefined)
        .map((c) => ({ key: c.name, engine, value: c.value, dataType: c.dataType })),
    [cells, engine],
  );

  useEffect(() => {
    let cancelled = false;
    if (items.length === 0) {
      setFeatures([]);
      return;
    }
    void decodeSpatialBatch(items, { maxBytesPerValue: MAX_EAGER_DECODE_BYTES })
      .then((results) => {
        if (cancelled) return;
        const next: SpatialMapFeature[] = [];
        for (const { key, result } of results) {
          if (result.ok) next.push({ id: key, geometry: result.geometry });
        }
        setFeatures(next);
      })
      .catch(() => {
        if (!cancelled) setFeatures([]);
      });
    return () => {
      cancelled = true;
    };
  }, [items]);

  const layers = useMemo<SpatialMapLayerInput[]>(() => {
    if (features.length === 0) return [];
    return [
      {
        def: {
          id: 'row-spatial',
          label: 'Geometry',
          visible: true,
          source: { kind: 'query', sql: '', geometryRef: { kind: 'column', column: '' } },
          style: { color: '#10b981' },
          featureCap: features.length,
        },
        features,
      },
    ];
  }, [features]);

  if (features.length === 0) return null;

  return (
    <div
      className="mx-2 mb-2 h-40 overflow-hidden rounded-md border border-border"
      data-testid="single-row-spatial-map"
    >
      <SpatialMapHost layers={layers} fitToData embedded />
    </div>
  );
}
